/**
 * useSoloPersonalBest — the requesting user's best-ever solo game AT a given time
 * control (best_points / best_score / best_combo + has_pb). Fetched at solo
 * battle mount to drive the stats-header "best" values and the race-track PB
 * ghost. Per-time-control (playing 90s → your best 90s game), so pass the live
 * session's timerSec.
 *
 * The Django endpoint returns the data object directly (GET
 * /fun_challenges/v1/solo_royale_personal_best/?time_control_sec=<n>).
 * `has_pb === false` → no prior game → the track shows the "set your first
 * record" flag instead of a ghost.
 */

import {useMemo} from 'react';
import {useQuery, useQueryClient} from '@tanstack/react-query';

import ChessRestClient from '@/Api/ChessRestClient';

export interface SoloPersonalBest {
  timeControlSec: number;
  bestPoints: number;
  bestScore: number;
  bestCombo: number;
  hasPb: boolean;
}

interface ApiPb {
  time_control_sec: number;
  best_points: number;
  best_score: number;
  best_combo: number;
  has_pb: boolean;
}

async function fetchSoloPersonalBest(
  timeControlSec: number,
): Promise<SoloPersonalBest> {
  const res = await ChessRestClient.get(
    '/fun_challenges/v1/solo_royale_personal_best/',
    {params: {time_control_sec: timeControlSec}},
  );
  const d = res.data as ApiPb;
  return {
    timeControlSec: d.time_control_sec,
    bestPoints: d.best_points ?? 0,
    bestScore: d.best_score ?? 0,
    bestCombo: d.best_combo ?? 0,
    hasPb: !!d.has_pb,
  };
}

export const soloPersonalBestKey = (timeControlSec: number) =>
  ['soloPersonalBest', timeControlSec] as const;

export default function useSoloPersonalBest(timeControlSec: number) {
  const query = useQuery<SoloPersonalBest, Error>(
    soloPersonalBestKey(timeControlSec),
    () => fetchSoloPersonalBest(timeControlSec),
    {
      enabled: Number.isFinite(timeControlSec) && timeControlSec > 0,
      staleTime: 60000,
    },
  );

  return useMemo(
    () => ({
      pb: query.data ?? null,
      isLoading: query.isLoading,
      refetch: query.refetch,
    }),
    [query.data, query.isLoading, query.refetch],
  );
}

/** SL-7: invalidate the PB after a solo game ends so the header/ghost pick up a
 *  new record on replay. */
export function useInvalidateSoloPersonalBest() {
  const qc = useQueryClient();
  return (timeControlSec?: number) =>
    qc.invalidateQueries({
      predicate: q => {
        const k = q.queryKey as unknown[];
        if (k[0] !== 'soloPersonalBest') {
          return false;
        }
        return timeControlSec == null || k[1] === timeControlSec;
      },
    });
}
